import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col, Alert, Spinner, Card } from 'react-bootstrap';
import apiClient from '../../../../../services/apiClient';
import AutocompleteSelect from '../../../../cabinet/wizplain/components/Autocomplete/AutocompleteSelect';
import FileUploader from '../../../../../components/FileUpload/FileUploader';
import { childTransferApi } from '../../../../../services/childTransferApi';
import { createNewDoc_KG, uploadFile } from '../DocumentsList/actions'; // Импортируем функции для работы с документами

// Типы мест
const placeTypeOptions = [
  { value: 'standart', label: 'Повного дня' },
  { value: 'without-bed', label: 'Короткотривалого перебування' },
  { value: 'inclusive', label: 'Інклюзивне повного дня' },
  { value: 'inclusive-short', label: 'Інклюзивне короткотривалого перебування' }
];

const ChildTransferModal = ({ 
  show, 
  onHide, 
  child, 
  kgId,
  currentGroup,
  onSuccess,
  allGroups,
  mode = "internal"
}) => {
  const [formData, setFormData] = useState({
    targetKindergarten: '',
    targetGroup: '',
    transferDate: '',
    placeType: '',
    orderNumber: '',
    orderDate: '',
    reason: '',
    documentName: ''
  });
  const [files, setFiles] = useState([]);
  const [availableGroups, setAvailableGroups] = useState(allGroups);
  const [availableKindergartens, setAvailableKindergartens] = useState([]);
  const [placeTypes, setPlaceTypes] = useState(placeTypeOptions);
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [currentStep, setCurrentStep] = useState('form');
  const [searchingKindergartens, setSearchingKindergartens] = useState(false);
  const [errors, setErrors] = useState({});

  // Конфигурация возрастных ограничений для перевода
  const typeGroupAgesTransfer = [
    { from: 1, to: 2, type: 2 }, 
    { from: 2, to: 3, type: 3 }, 
    { from: 3, to: 4, type: 4 }, 
    { from: 4, to: 5, type: 5 }, 
    { from: 5, to: 9, type: 6 },
    { from: 6, to: 7, type: 7 }, 
    { from: 3, to: 5, type: 10 }, 
    { from: 3, to: 9, type: 11 }, 
    { from: 4, to: 9, type: 12 },
    { from: 1.5, to: 3, type: 14 }, 
    { from: 0, to: 10, type: 15 },
    { from: 1, to: 3, type: 16 }, 
    { from: 0.6, to: 1, type: 17 }
  ];

  // Сброс формы при открытии/закрытии модалки
  useEffect(() => {
    if (show) {
      resetForm();
      if (mode === "external") {
        loadInitialKindergartens();
      } else {
        setAvailableGroups(allGroups)
      }
    }
  }, [show, child, mode]);

  const resetForm = () => {
    setFormData({
      targetKindergarten: '',
      targetGroup: '',
      transferDate: new Date().toISOString().split('T')[0],
      placeType: '',
      orderNumber: '',
      orderDate: '',
      reason: '',
      documentName: ''
    });
    setFiles([]);
    setSubmitError('');
    setCurrentStep('form');
    setAvailableGroups([]);
    setErrors({});
  };

  // Загрузка начального списка садиков
  const loadInitialKindergartens = async () => {
    try {
      setSearchingKindergartens(true);
      const response = await apiClient.get('kindergartens/list?limit=10');
      if (response.data) {
        setAvailableKindergartens(response.data);
      }
    } catch (error) {
      console.error('Error loading kindergartens:', error);
    } finally {
      setSearchingKindergartens(false);
    }
  };

  // Функция для поиска садиков
  const handleKindergartenSearch = async (search) => {
    if (!search) return;
    
    try {
      setSearchingKindergartens(true);
      const response = await apiClient.get(`kindergartens/list?search=${encodeURIComponent(search)}&limit=20`);
      if (response.data) {
        setAvailableKindergartens(response.data);
      }
    } catch (error) {
      console.error('Error searching kindergartens:', error);
    } finally {
      setSearchingKindergartens(false);
    }
  };

  // Загрузка групп выбранного садика
  const loadGroupsForKindergarten = async (kindergartenId) => {
    if (!kindergartenId) {
      setAvailableGroups([]);
      return;
    }

    try {
      setLoading(true);
      const response = await apiClient.get(`kindergartens/${kindergartenId}`);
      if (response.data && response.data[0]) {
        const childAge = calculateChildAge(child.birthDate);
        const filteredGroups = response.data[0].groups;//.filter(group => {
        //  const ageConfig = typeGroupAgesTransfer.find(config => config.type === group.typeNumber);
        //  if (!ageConfig) return false;
        //  return childAge >= ageConfig.from && childAge < ageConfig.to;
        //});
        setAvailableGroups(filteredGroups);
      }
    } catch (error) {
      console.error('Error loading groups for kindergarten:', error);
      setSubmitError('Помилка завантаження списку груп для обраного садочка');
      setAvailableGroups([]);
    } finally {
      setLoading(false);
    }
  };

  const calculateChildAge = (birthDate) => {
    const birth = new Date(birthDate);
    const today = new Date();
    const ageInMonths = (today.getFullYear() - birth.getFullYear()) * 12 + (today.getMonth() - birth.getMonth());
    return ageInMonths / 12;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Если меняется выбранный садик, загружаем его группы
    if (field === 'targetKindergarten' && mode === "external") {
      setFormData(prev => ({
        ...prev,
        targetGroup: ''
      }));
      loadGroupsForKindergarten(value);
    }
    
    // Автоматическое формирование названия документа для внешнего перевода
    if ((field === 'orderNumber' || field === 'reason') && !formData.documentName && mode === "external") {
      const newDocumentName = `Наказ №${field === 'orderNumber' ? value : formData.orderNumber} - Переведення до іншого садочка`;
      setFormData(prev => ({
        ...prev,
        documentName: newDocumentName
      }));
    }
    
    // Очистка ошибки при изменении поля
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
    if (submitError) {
      setSubmitError('');
    }
  };

  const handleFileChange = (newFiles) => {
    setFiles(newFiles);
    
    // Очистка ошибки файлов при изменении
    if (errors.files) {
      setErrors(prev => ({
        ...prev,
        files: ''
      }));
    }
  };

  // Обработчик клика по форме - предотвращает сабмит при клике на кнопки внутри FileUploader
  const handleFormClick = (e) => {
    // Проверяем, был ли клик на кнопке внутри FileUploader
    const target = e.target;
    const isFileUploaderButton = target.closest('.file-uploader-group .btn, .file-uploader-group button, .file-uploader-group .action-link');
    const isFileUploaderCardAction = target.closest('.uploaded-file-card .file-actions button');
    
    if (isFileUploaderButton || isFileUploaderCardAction) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  // Обработчик keydown для предотвращения сабмита по Enter
  const handleFormKeyDown = (e) => {
    if (e.key === 'Enter') {
      const activeElement = document.activeElement;
      const isFileUploaderButton = activeElement.closest('.file-uploader-group .btn, .file-uploader-group button');
      
      if (isFileUploaderButton) {
        e.preventDefault();
        e.stopPropagation();
      }
    }
  };

  // Валидация формы
  const validateForm = () => {
    const newErrors = {};

    if (mode === "external" && !formData.targetKindergarten) {
      newErrors.targetKindergarten = 'Оберіть садочок для переведення';
    }

    if (!formData.targetGroup) {
      newErrors.targetGroup = 'Оберіть групу для переведення';
    }

    if (!formData.transferDate) {
      newErrors.transferDate = 'Вкажіть дату переведення';
    }

    if (!formData.placeType) {
      newErrors.placeType = 'Оберіть тип місця';
    }

    const transferDate = new Date(formData.transferDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (transferDate < today) {
      newErrors.transferDate = 'Дата переведення не може бути у минулому';
    }

    // Валидация полей документа для внешнего перевода
    if (mode === "external") {
      if (!formData.orderNumber || formData.orderNumber.trim() === '') {
        newErrors.orderNumber = 'Номер наказу обов\'язковий';
      }

      if (!formData.orderDate) {
        newErrors.orderDate = 'Дата наказу обов\'язкова';
      }

      if (!formData.reason || formData.reason.trim() === '') {
        newErrors.reason = 'Причина переведення обов\'язкова';
      }

      if (!files || files.length === 0) {
        newErrors.files = 'Скан-копія наказу обов\'язкова';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Создание документа для перевода (как в ChildDismissalModal)
  const createTransferDocument = async (documentData) => {
    try {
      const docData = {
        target_id: kgId,
        type: 'transfer_order', // специальный тип для приказов на перевод
        name: documentData.documentName,
        description: `Наказ на перевод ребенка в другой садик. ${documentData.reason}`
      };
      
      const result = await createNewDoc_KG(docData);
      return result.data; // возвращаем созданный документ с ID
    } catch (error) {
      console.error('Error creating transfer document:', error);
      throw new Error('Не вдалося створити документ переведення');
    }
  };

  // Загрузка файла в документ (как в ChildDismissalModal)
  const uploadTransferFile = async (docId, file) => {
    try {
      const fileInfo = {
        doc_id: docId,
        name: file.name,
        mtype: file.type,
        length: file.size,
        date: new Date().toISOString()
      };

      // Конвертируем base64 в ArrayBuffer для загрузки
      const fileBuffer = base64ToArrayBuffer(file.file);
      const result = await uploadFile(fileInfo, fileBuffer);
      return result;
    } catch (error) {
      console.error('Error uploading transfer file:', error);
      throw new Error('Не вдалося завантажити файл');
    }
  };

  // Вспомогательная функция для конвертации base64 в ArrayBuffer
  const base64ToArrayBuffer = (base64) => {
    const binaryString = atob(base64.split(',')[1]);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes.buffer;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setCurrentStep('processing');

    try {
      const selectedGroup = availableGroups.find(group => group._id === formData.targetGroup);
      const selectedKindergarten = mode === "external" 
        ? availableKindergartens.find(kg => kg._id === formData.targetKindergarten)
        : null;
      
      // Подтверждение действия
      const confirmationMessage = mode === "external" 
        ? `Ви переводите дитину до садочка "${selectedKindergarten?.name}" в групу №${selectedGroup.number} на тип місця "${placeTypeOptions.find(pt => pt.value === formData.placeType)?.label}" з ${new Date(formData.transferDate).toLocaleDateString('uk-UA')}`
        : `Ви переводите дитину в групу №${selectedGroup.number} на тип місця "${placeTypeOptions.find(pt => pt.value === formData.placeType)?.label}" з ${new Date(formData.transferDate).toLocaleDateString('uk-UA')}`;

      if (!window.confirm(confirmationMessage)) {
        setLoading(false);
        setCurrentStep('form');
        return;
      }

      let documentId = null;

      // Для внешнего перевода создаем документ и загружаем файлы (как в ChildDismissalModal)
      if (mode === "external") {
        // 1. Создаем документ в системе документов садика
        const documentData = {
          documentName: formData.documentName || `Наказ №${formData.orderNumber} - Переведення до іншого садочка`,
          reason: formData.reason
        };

        const createdDocument = await createTransferDocument(documentData);
        
        if (!createdDocument || !createdDocument._id) {
          throw new Error('Не вдалося створити документ переведення');
        }

        documentId = createdDocument._id;

        // 2. Загружаем файлы в созданный документ
        const fileUploadPromises = files.map(file => 
          uploadTransferFile(documentId, file)
        );

        await Promise.all(fileUploadPromises);
      }

      if (mode === "external") {
        // Используем API временного перемещения для внешнего перевода
        const transferData = {
          targetKindergarten: formData.targetKindergarten,
          targetGroup: formData.targetGroup,
          currentGroup: currentGroup._id,
          childId: child.id,
          documentName: formData.documentName || `Наказ №${formData.orderNumber} - Переведення до іншого садочка`,
          orderNumber: formData.orderNumber,
          orderDate: formData.orderDate,
          reason: formData.reason,
          placeType: formData.placeType,
          targetGroupNumber: selectedGroup.number,
          scan_doc: documentId // Передаем ID созданного документа
        };

        const result = await childTransferApi.addTemporaryTransfer(transferData);
        
        if (result && result.mv && result.kg) {
          setCurrentStep('complete');
          setTimeout(() => {
            onSuccess();
            onHide();
          }, 2000);
        } else {
          throw new Error('Помилка переведення');
        }
      } else {
        // Оригинальный запрос для внутреннего перевода
        const url = `kindergartens/${kgId}/groups/${encodeURIComponent(currentGroup._id)}/places/child/${child.id}/transfer/groups/${encodeURIComponent(formData.targetGroup)}?dateFrom=${formData.transferDate}&placeType=${formData.placeType}`;

        const response = await apiClient.post(url, {});

        if (response.data) {
          setCurrentStep('complete');
          setTimeout(() => {
            onSuccess();
            onHide();
          }, 2000);
        } else {
          throw new Error(response.error?.message || 'Помилка переведення');
        }
      }
      
    } catch (error) {
      console.error('Error submitting transfer:', error);
      setSubmitError(error.message || 'Помилка при оформленні переведення. Спробуйте ще раз.');
      setCurrentStep('form');
    } finally {
      setLoading(false);
    }
  };

  const getCurrentDate = () => {
    return new Date().toISOString().split('T')[0];
  };

  const getTargetGroupName = () => {
    return availableGroups.find(group => group._id === formData.targetGroup)?.name || '';
  };

  const getTargetKindergartenName = () => {
    return availableKindergartens.find(kg => kg._id === formData.targetKindergarten)?.name || '';
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 'processing':
        return (
          <div className="text-center py-4">
            <Spinner animation="border" role="status" className="mb-3">
              <span className="visually-hidden">Обробка...</span>
            </Spinner>
            <h5>Обробка запиту...</h5>
            <p>Виконується переведення дитини{mode === "external" && ' та створення документів'}</p>
          </div>
        );

      case 'complete':
        return (
          <div className="text-center py-4">
            <div className="text-success mb-3">
              <i className="bi bi-check-circle-fill" style={{ fontSize: '3rem' }}></i>
            </div>
            <h5>Переведення успішно оформлено!</h5>
            <p>
              {mode === "external" 
                ? <>Дитину переведено до садочка: <strong>{getTargetKindergartenName()}</strong>, група: <strong>{getTargetGroupName()}</strong></>
                : <>Дитину переведено до групи: <strong>{getTargetGroupName()}</strong></>
              }
            </p>
            {mode === "external" && <p>Документ переведення створено та файли завантажено</p>}
          </div>
        );

      default:
        return renderFormContent();
    }
  };

  const renderDocumentFields = () => {
    if (mode !== "external") return null;

    return (
      <>
        <hr />
        <h6 className="mb-3">Документи переведення</h6>
        
        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>
                Номер наказу <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                value={formData.orderNumber}
                onChange={(e) => handleInputChange('orderNumber', e.target.value)}
                isInvalid={!!errors.orderNumber}
                placeholder="Введіть номер наказу"
                disabled={loading}
              />
              <Form.Control.Feedback type="invalid">
                {errors.orderNumber}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>

          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>
                Дата наказу <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="date"
                value={formData.orderDate}
                onChange={(e) => handleInputChange('orderDate', e.target.value)}
                isInvalid={!!errors.orderDate}
                max={getCurrentDate()}
                disabled={loading}
              />
              <Form.Control.Feedback type="invalid">
                {errors.orderDate}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
        </Row>

        <Form.Group className="mb-3">
          <Form.Label>
            Причина переведення <span className="text-danger">*</span>
          </Form.Label>
          <Form.Control
            as="textarea"
            rows={2}
            value={formData.reason}
            onChange={(e) => handleInputChange('reason', e.target.value)}
            isInvalid={!!errors.reason}
            placeholder="Вкажіть причину переведення дитини до іншого садочка"
            disabled={loading}
          />
          <Form.Control.Feedback type="invalid">
            {errors.reason}
          </Form.Control.Feedback>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>
            Назва документа <span className="text-danger">*</span>
          </Form.Label>
          <Form.Control
            type="text"
            value={formData.documentName}
            onChange={(e) => handleInputChange('documentName', e.target.value)}
            placeholder="Назва документа для системи"
            disabled={loading}
          />
          <Form.Text className="text-muted">
            Автоматично формується з номера наказу
          </Form.Text>
        </Form.Group>

        <FileUploader
          label="Скан-копія наказу на переведення"
          description="Завантажте скан-копію наказу у форматі PDF, JPG, PNG. Максимальний розмір файлу 2 МБ."
          value={files}
          onChange={handleFileChange}
          required={true}
          disabled={loading}
          multiple={false}
          errorVal={errors.files}
        />
      </>
    );
  };

  const renderFormContent = () => {
    if (!child) return null;

    return (
      <>
        {/* Информация о ребенке */}
        <Card className="mb-4">
          <Card.Header>
            <h6 className="mb-0">Інформація про дитину</h6>
          </Card.Header>
          <Card.Body>
            <Row>
              <Col md={4}>
                <strong>ПІБ:</strong><br />
                {child.lastName} {child.firstName} {child.middleName}
              </Col>
              <Col md={4}>
                <strong>Поточна група:</strong><br />
                {currentGroup?.name || child.group}
              </Col>
              <Col md={4}>
                <strong>Дата народження:</strong><br />
                {child.birthDate}
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {submitError && (
          <Alert variant="danger" className="mb-3">
            {submitError}
          </Alert>
        )}

        {/* Поле выбора садика для внешнего перевода */}
        {mode === "external" && (
          <Form.Group className="mb-3">
            <Form.Label>
              Садочок для переведення <span className="text-danger">*</span>
            </Form.Label>
            <AutocompleteSelect 
              value={formData.targetKindergarten} 
              options={availableKindergartens} 
              onSearch={handleKindergartenSearch} 
              onChange={(value) => handleInputChange('targetKindergarten', value)}
              placeholder='Оберіть садочок'
              loading={searchingKindergartens}
              label=""
              className="small"
              fieldsValue={["number", "name"]}
            />
            {errors.targetKindergarten && (
              <div className="invalid-feedback d-block">
                {errors.targetKindergarten}
              </div>
            )}
            <Form.Text className="text-muted">
              Введіть назву або номер садочка для пошуку
            </Form.Text>
          </Form.Group>
        )}

        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>
                Група для переведення <span className="text-danger">*</span>
              </Form.Label>
              <Form.Select
                value={formData.targetGroup}
                onChange={(e) => handleInputChange('targetGroup', e.target.value)}
                disabled={loading || (mode === "external" && !formData.targetKindergarten)}
                isInvalid={!!errors.targetGroup}
              >
                <option value="">Оберіть групу</option>
                {(availableGroups || []).map(group => (
                  <option key={group._id} value={group._id}>
                    №{group.number} - {group.name} ({group.type})
                  </option>
                ))}
              </Form.Select>
              <Form.Control.Feedback type="invalid">
                {errors.targetGroup}
              </Form.Control.Feedback>
              <Form.Text className="text-muted">
                {mode === "external" 
                  ? "Доступні групи обраного садочка з урахуванням вікових обмежень"
                  : "Доступні групи з урахуванням вікових обмежень"
                }
              </Form.Text>
            </Form.Group>
          </Col>

          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>
                Тип місця <span className="text-danger">*</span>
              </Form.Label>
              <Form.Select
                value={formData.placeType}
                onChange={(e) => handleInputChange('placeType', e.target.value)}
                disabled={loading}
                isInvalid={!!errors.placeType}
              >
                <option value="">Оберіть тип місця</option>
                {placeTypes.map(type => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </Form.Select>
              <Form.Control.Feedback type="invalid">
                {errors.placeType}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
        </Row>

        <Form.Group className="mb-4">
          <Form.Label>
            Дата переведення <span className="text-danger">*</span>
          </Form.Label>
          <Form.Control
            type="date"
            value={formData.transferDate}
            onChange={(e) => handleInputChange('transferDate', e.target.value)}
            min={getCurrentDate()}
            disabled={loading}
            isInvalid={!!errors.transferDate}
          />
          <Form.Control.Feedback type="invalid">
            {errors.transferDate}
          </Form.Control.Feedback>
          <Form.Text className="text-muted">
            Дата, з якої дитина починає відвідувати нову {mode === "external" ? "садочок" : "групу"}
          </Form.Text>
        </Form.Group>

        {/* Поля документа для внешнего перевода */}
        {renderDocumentFields()}

        {/* Информационный блок */}
        <Alert variant="info" className="mt-3">
          <strong>Увага!</strong> Після переведення дитини {mode === "external" ? "до нового садочка" : "до нової групи"}:
          <ul className="mb-0 mt-2">
            <li>Дитина буде автоматично {mode === "external" ? "переміщена до обраного садочка" : "переміщена до обраної групи"}</li>
            <li>Всі дані про відвідування будуть збережені</li>
            {mode === "external" && (
              <>
                <li>Для завершення переведення може знадобитися підтвердження з боку нового садочка</li>
                <li>Наказ про перевод буде збережено в системі документів</li>
              </>
            )}
          </ul>
        </Alert>
      </>
    );
  };

  return (
    <Modal 
      show={show} 
      onHide={onHide} 
      size="lg" 
      centered
      backdrop="static"
    >
      <Modal.Header closeButton>
        <Modal.Title>
          {mode === "external" ? "Переведення дитини до іншого садочка" : "Переведення дитини до іншої групи"}
        </Modal.Title>
      </Modal.Header>

      <Form 
        onSubmit={handleSubmit}
        onClick={handleFormClick}
        onKeyDown={handleFormKeyDown}
      >
        <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {renderStepContent()}
        </Modal.Body>

        {currentStep === 'form' && (
          <Modal.Footer>
            <Button 
              variant="secondary" 
              onClick={onHide}
              disabled={loading}
              type="button"
            >
              Скасувати
            </Button>
            <Button 
              variant="primary" 
              type="submit"
              disabled={loading}
            >
              {loading ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                    className="me-2"
                  />
                  Обробка...
                </>
              ) : (
                `Підтвердити переведення`
              )}
            </Button>
          </Modal.Footer>
        )}

        {currentStep === 'complete' && (
          <Modal.Footer>
            <Button variant="success" disabled type="button">
              Успішно завершено
            </Button>
          </Modal.Footer>
        )}
      </Form>
    </Modal>
  );
};

export default ChildTransferModal;